/**
 * Common validation utilities
 */

/**
 * Validates that required fields are present and non-empty
 */
export function validateRequiredFields(
  fields: Record<string, unknown>
): string | null {
  for (const [fieldName, value] of Object.entries(fields)) {
    const isFalsy = !value;
    const isStringAndEmpty = typeof value === 'string' && value.trim() === '';
    if (isFalsy || isStringAndEmpty) {
      return `${fieldName} is required`;
    }
  }
  return null;
}

/**
 * Validates required parameters in request body
 */
export function validateRequiredParams<T extends Record<string, unknown>>(
  body: T,
  requiredParams: (keyof T)[]
): {
  isValid: boolean;
  missingParams?: string[];
} {
  const missingParams = requiredParams.filter(
    param => body[param] === undefined
  );

  if (missingParams.length > 0) {
    return { isValid: false, missingParams: missingParams as string[] };
  }

  return { isValid: true };
}

/**
 * Validates age (must be 18 or older)
 */
export function validateAge(age: string | number): string | null {
  const parsedAge = typeof age === 'string' ? parseInt(age, 10) : age;

  const isInvalidAge = isNaN(parsedAge);
  const isUnderage = parsedAge < 18;
  if (isInvalidAge || isUnderage) {
    return 'You must be at least 18 years old';
  }

  return null;
}

/**
 * Validates confirmation code (must be 6 characters)
 */
export function validateConfirmationCode(code: string): string | null {
  if (!code || code.trim() === '') {
    return 'Confirmation code cannot be empty';
  }

  if (code.length < 6) {
    return 'The code needs to be 6 characters long';
  }

  return null;
}

/**
 * Validates preferred name (must be at least 3 characters)
 */
export function validatePreferredName(name: string): string | null {
  if (!name || name.trim() === '') {
    return 'Preferred name is required';
  }

  if (name.length < 3) {
    return 'Preferred name must be at least 3 characters';
  }

  return null;
}
